import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useCatalogService } from '../../service-administration/services/catalogService';
import { landscapeKeys } from './useLandscapeMatrix';
import type { Deployment, DeploymentHistory } from '../../../types/backend';

export interface SpokeActivityEntry extends DeploymentHistory {
  /** product_name of the deployment this event belongs to (denormalised for display). */
  product: string;
}

export interface SpokeActivityDay {
  /** ISO date (YYYY-MM-DD). */
  date: string;
  entries: SpokeActivityEntry[];
}

export interface SpokeActivity {
  days: SpokeActivityDay[];
  total: number;
  loading: boolean;
  error: string | null;
}

// Stable empty reference so the day-grouping memo below is stable while the
// query is disabled or has no data yet.
const EMPTY_ENTRIES: SpokeActivityEntry[] = [];

/**
 * Merges per-deployment history (deployments.GetHistory) for every deployment in a spoke
 * into one timeline, newest first, grouped by day. There is no spoke-level activity RPC —
 * this composes the existing per-deployment one.
 */
export function useSpokeActivity(
  customerId: string | undefined,
  deployments: Deployment[],
  active: boolean,
): SpokeActivity {
  const catalogService = useCatalogService();

  // The deployment set keys the cache: reopening the same spoke is served from
  // cache, a different set refetches.
  const ids = deployments.map((d) => d.id).join(',');
  const enabled = active && !!customerId && deployments.length > 0;

  const query = useQuery({
    queryKey: landscapeKeys.spokeActivity(customerId, ids),
    queryFn: async () => {
      const nameById = new Map(deployments.map((d) => [d.id, d.product_name ?? d.product_id]));
      const lists = await Promise.all(
        deployments.map((d) =>
          catalogService.getDeploymentHistory(customerId as string, d.id)
            .then((r) => r.history.map<SpokeActivityEntry>((h) => ({ ...h, product: nameById.get(d.id) ?? '' })))
            .catch(() => [] as SpokeActivityEntry[]),
        ),
      );
      return lists.flat().sort((a, b) => b.timestamp.localeCompare(a.timestamp));
    },
    enabled,
  });

  const entries = enabled ? (query.data ?? EMPTY_ENTRIES) : EMPTY_ENTRIES;

  const days = useMemo<SpokeActivityDay[]>(() => {
    const byDay = new Map<string, SpokeActivityEntry[]>();
    entries.forEach((e) => {
      const date = e.timestamp.slice(0, 10);
      const arr = byDay.get(date);
      if (arr) arr.push(e);
      else byDay.set(date, [e]);
    });
    return [...byDay.entries()]
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([date, dayEntries]) => ({ date, entries: dayEntries }));
  }, [entries]);

  const error = enabled && query.error
    ? (query.error instanceof Error ? query.error.message : 'Failed to load activity')
    : null;

  return { days, total: entries.length, loading: query.isFetching, error };
}
